import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import declineIcon from '../../images/decline.svg';
import { bookingService, talentService, userService, emailService } from '../../api/apiService';
import { customStyles } from './../../helpers/StyleUtils';

const TalentsOnHoldList = ({ events, authToken, initialWeek }) => {
    // Debug logging to see what events are received
    console.log('TalentsOnHoldList received events:', events?.length || 0);
    console.log('First event sample:', events?.[0]);
    
    // Additional debugging for talent field
    if (events && events.length > 0) {
        console.log('Talent field values:', events.map(event => ({
            id: event.id,
            talent: event.talent,
            talent_name: event.talent_name,
            talentName: event.talentName,
            hasTalentField: !!event.talent || !!event.talent_name || !!event.talentName
        })));
    }
    
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedKPI, setSelectedKPI] = useState(null);
    const [selectedGrup, setSelectedGrup] = useState(null);
    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedSet, setSelectedSet] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [selectedPosition, setSelectedPosition] = useState(null);
    const [selectedTalent, setSelectedTalent] = useState(null);
    const [filterPreferences, setFilterPreferences] = useState([]);
    const [selectedPreferenceIndex, setSelectedPreferenceIndex] = useState(null); 
    const [selectedOrders, setSelectedOrders] = useState({});
    const [notifyTalent, setNotifyTalent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentWeek, setCurrentWeek] = useState(initialWeek || getCurrentWeek());
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    
    let userEmail = localStorage.getItem("userEmail");

    useEffect(() => {
        const fetchPreferences = async () => {
            if (!userEmail) return;

            try {
                const response = await userService.getUserPreferences(userEmail);
                
                if (response && response.data && response.data.filterPreferences) {
                    setFilterPreferences(response.data.filterPreferences);
                } else {
                    console.log("No user preferences found.");
                }
            } catch (error) {
                console.error("Error fetching user preferences:", error);
            }
        };

        fetchPreferences();
    }, [userEmail]);

    const handleChange = event => {
        setSearchTerm(event.target.value);
    };
    
    const handleCheckboxChange = (index) => {
        setSelectedOrders((prevSelected) => ({
          ...prevSelected,
          [index]: !prevSelected[index],
        }));
    };
    
    const fetchBooking = async (bookingId) => {
        try {
            console.log(`Fetching booking with ID: ${bookingId}`);
            const response = await bookingService.getBookingById(bookingId);
            console.log(`Booking API response for ID ${bookingId}:`, response);
            
            if (!response) {
                console.error(`Booking not found - No response for ID: ${bookingId}`);
                return null;
            }
            
            if (!response.data) {
                console.error(`Booking not found - No data in response for ID: ${bookingId}`);
                return null;
            }
            
            // Check if the booking data is directly in response.data or nested under response.data.booking
            const bookingData = response.data.booking || response.data;
            
            if (!bookingData) {
                console.error(`Booking not found - No booking data for ID: ${bookingId}`);
                console.log('Response data structure:', response.data);
                return null;
            }
            
            console.log(`Successfully fetched booking ${bookingId}:`, bookingData);
            return bookingData;
        } catch (error) {
            console.error(`Error fetching booking ${bookingId}:`, error);
            console.error('Error details:', error.response?.data || error.message);
            return null;
        }
    };
    
    const declineSelectedTalents = async () => {
        const selectedOrderIds = Object.keys(selectedOrders).filter(
            (id) => selectedOrders[id]
        );
      
        if (selectedOrderIds.length === 0) {
            console.log("No orders selected.");
            return;
        }

        const userConfirmed = window.confirm("Are you sure you want to release the selected talents?");
        if (userConfirmed) {
            setIsLoading(true);
            setError(null);
            
            let releaseErrors = [];
            let emailErrors = [];
            let successfulReleases = 0;
            
            try {
                const updates = selectedOrderIds.map(async (bookingId) => {
                    let booking = null;
                    let talent = null;
                    
                    try {
                        // Fetch booking data
                        booking = await fetchBooking(bookingId);
                        if (!booking) {
                            console.warn(`Could not find booking with ID: ${bookingId}, but will attempt status update anyway`);
                        }
                        
                        // Fetch talent data if available
                        if (booking && booking.talent_id) {
                            console.log(`🔍 DEBUG: Fetching talent data for ID: ${booking.talent_id}`);
                            try {
                                const talentResponse = await talentService.getTalentById(booking.talent_id);
                                
                                // Check multiple possible response structures
                                if (talentResponse?.data?.talent) {
                                    talent = talentResponse.data.talent;
                                } else if (talentResponse?.data?.Name || talentResponse?.data?.name) {
                                    talent = talentResponse.data;
                                } else if (talentResponse?.talent) {
                                    talent = talentResponse.talent;
                                } else if (talentResponse?.data) {
                                    talent = talentResponse.data;
                                }
                                
                                console.log(`🔍 DEBUG: Talent data found:`, !!talent);
                            } catch (error) {
                                console.warn(`Could not fetch talent data for ${booking.talent_id}:`, error.message);
                            }
                        }
                        
                        // CRITICAL: Update booking status to canceled - this is the main operation
                        console.log(`Updating booking ${bookingId} status to canceled`);
                        await bookingService.updateBookingStatus(bookingId, 'canceled');
                        successfulReleases++;
                        console.log(`✅ Successfully released booking ${bookingId}`);
                        
                    } catch (error) {
                        console.error(`❌ CRITICAL ERROR: Failed to release booking ${bookingId}:`, error);
                        releaseErrors.push(`Booking ${bookingId}: ${error.message}`);
                        return; // Skip email sending if release failed
                    }
                    
                    // NON-CRITICAL: Send email notifications (don't fail the whole operation if this fails)
                    if (notifyTalent) {
                        try {
                            if (talent) {
                                console.log(`📧 EMAIL: Sending OPTION RELEASE email for booking ${bookingId}`);
                                
                                const emailData = {
                                    to_email: ['booking@set-cast.com' ,'fitore.gashi@mondula.com', talent.email || talent.E_Mail || ''],
                                    subject: 'OPTION RELEASE',
                                    content: createEmailContentForDecline(booking, talent),
                                    cc: ['booking@set-cast.com']
                                };
                                
                                await emailService.sendEmail(emailData);
                                console.log(`✅ EMAIL SENT successfully for booking ${bookingId}`);
                            } else {
                                console.log(`📧 EMAIL: Sending OPTION RELEASE email for booking ${bookingId} (no talent data)`);
                                
                                const emailData = {
                                    to_email: ['booking@set-cast.com', 'fitore.gashi@mondula.com'],
                                    subject: 'OPTION RELEASE - No Talent Data',
                                    content: createEmailContentForDeclineNoTalent(booking),
                                    cc: ['booking@set-cast.com']
                                };
                                
                                await emailService.sendEmail(emailData);
                                console.log(`✅ EMAIL SENT successfully for booking ${bookingId} (no talent data)`);
                            }
                        } catch (error) {
                            console.warn(`⚠️ EMAIL ERROR for booking ${bookingId}:`, error.message);
                            emailErrors.push(`Booking ${bookingId}: ${error.message}`);
                        }
                    } else {
                        console.log(`🔍 DEBUG: Email not sent for booking ${bookingId} - notifyTalent disabled`);
                    }
                });

                await Promise.all(updates);
                
                // Show appropriate success/warning messages
                if (releaseErrors.length === 0) {
                    let message = `${successfulReleases} talent(s) released successfully.`;
                    if (emailErrors.length > 0) {
                        message += `\n\nNote: Some emails failed to send but talents were released:\n${emailErrors.join('\n')}`;
                    }
                    alert(message);
                } else {
                    // Some releases failed
                    let message = '';
                    if (successfulReleases > 0) {
                        message += `${successfulReleases} talent(s) released successfully.\n\n`;
                    }
                    message += `❌ FAILED to release ${releaseErrors.length} talent(s):\n${releaseErrors.join('\n')}`;
                    
                    if (emailErrors.length > 0) {
                        message += `\n\nAdditional email errors:\n${emailErrors.join('\n')}`;
                    }
                    
                    setError(message);
                    alert(message);
                }
                
                window.location.reload();
            } catch (error) {
                console.error("Unexpected error in release operation:", error);
                setError(`Unexpected error occurred: ${error.message}`);
            } finally {
                setIsLoading(false);
            }
        } else {
            console.log("You have cancelled the operation.");
        }
    };

    const fetchTalent = async (talentId) => {
        try {
            if (!talentId) return null;
            
            const response = await talentService.getTalentById(talentId);
            
            if (!response || !response.data || !response.data.talent) {
                console.error("Talent not found");
                return null;
            }
            
            return response.data.talent;
        } catch (error) {
            console.error("Error fetching talent:", error);
            return null;
        }
    };

    const acceptporposedtalent = async (bookingId) => {
        const userConfirmed = window.confirm("Are you sure you want to confirm?");
        if (userConfirmed) {
            setIsLoading(true);
            setError(null);
            
            try {
                const booking = await fetchBooking(bookingId);
                
                if (!booking) {
                    throw new Error(`Could not find booking with ID: ${bookingId}`);
                }
                
                let talent = null;
                if (booking.talent_id) {
                    const talentResponse = await talentService.getTalentById(booking.talent_id);
                    if (talentResponse && talentResponse.data && talentResponse.data.talent) {
                        talent = talentResponse.data.talent;
                    }
                }
                
                // Update booking status to Booked using the API
                await bookingService.updateBookingStatus(bookingId, 'Booked');
                
                // Send confirmation emails
                if (talent) {
                    console.log(`📧 EMAIL TRIGGERED: Sending CONFIRMATION emails for booking ${bookingId} to talent ${talent.email || talent.E_Mail}`);
                    
                    // Email to booking team
                    const teamEmailData = {
                        to_email: ['booking@set-cast.com', 'talent@mondula.com, fitore.gashi@mondula.com'],
                        subject: 'Planning Orendt has accepted the booking',
                        content: createEmailContentForTeamConfirmation(booking, talent),
                        cc: ['booking@set-cast.com']
                    };
                    
                    // Email to talent
                    const talentEmailData = {
                        to_email: ['booking@set-cast.com', 'talent@mondula.com', 'fitore.gashi@mondula.com', talent.email || talent.E_Mail || ''],
                        subject: 'Planning Orendt has accepted the booking',
                        content: createEmailContentForTalentConfirmation(booking, talent),
                        cc: ['booking@set-cast.com']
                    };
                    
                    console.log(`📧 TEAM EMAIL DATA:`, teamEmailData);
                    await emailService.sendEmail(teamEmailData);
                    console.log(`✅ TEAM EMAIL SENT: Confirmation email sent to team for booking ${bookingId}`);
                    
                    console.log(`📧 TALENT EMAIL DATA:`, talentEmailData);
                    await emailService.sendEmail(talentEmailData);
                    console.log(`✅ TALENT EMAIL SENT: Confirmation email sent to talent for booking ${bookingId}`);
                }
                
                alert("Booking updated successfully.");
            } catch (error) {
                console.error("Error confirming booking:", error);
                setError(`Failed to confirm booking: ${error.message}`);
            } finally {
                setIsLoading(false);
            }
        } else {
            console.log("You have cancelled the operation.");
        }
    };

    const declineproposedtalent = async (bookingId) => {
        const userConfirmed = window.confirm("Are you sure you want to release this talent?");
        if (userConfirmed) {
            setIsLoading(true);
            setError(null);
            
            let booking = null;
            let talent = null;
            let emailError = null;
            
            try {
                console.log(`Attempting to release talent for booking ID: ${bookingId}`);
                
                // Try to fetch the booking first
                try {
                    booking = await fetchBooking(bookingId);
                    if (booking && booking.talent_id) {
                        const talentResponse = await talentService.getTalentById(booking.talent_id);
                        if (talentResponse && talentResponse.data && talentResponse.data.talent) {
                            talent = talentResponse.data.talent;
                        }
                    }
                } catch (error) {
                    console.warn(`Could not fetch booking/talent data for ${bookingId}:`, error.message);
                }
                
                // CRITICAL: Update booking status to canceled - this is the main operation
                console.log(`Updating booking ${bookingId} status to canceled`);
                await bookingService.updateBooking(bookingId, {
                    status: 'canceled',
                    talent_id: null // Remove talent assignment
                });
                console.log(`✅ Successfully released booking ${bookingId}`);
                
            } catch (error) {
                console.error(`❌ CRITICAL ERROR: Failed to release booking ${bookingId}:`, error);
                setError(`Failed to release talent: ${error.message}`);
                return;
            }
            
            // NON-CRITICAL: Send email notifications (don't fail if this fails)
            if (notifyTalent) {
                try {
                    if (talent) {
                        console.log(`📧 EMAIL: Sending OPTION RELEASE email for booking ${bookingId}`);
                        
                        const emailData = {
                            to_email: ['booking@set-cast.com', 'fitore.gashi@mondula.com', talent.E_Mail || talent.email || ''],
                            subject: 'OPTION RELEASE',
                            content: createEmailContentForDecline(booking, talent),
                            cc: ['booking@set-cast.com']
                        };
                        
                        await emailService.sendEmail(emailData);
                        console.log(`✅ EMAIL SENT successfully for booking ${bookingId}`);
                    } else {
                        console.log(`📧 EMAIL: Sending OPTION RELEASE email for booking ${bookingId} (no talent data)`);
                        
                        const emailData = {
                            to_email: ['booking@set-cast.com', 'fitore.gashi@mondula.com'],
                            subject: 'OPTION RELEASE - No Talent Data',
                            content: createEmailContentForDeclineNoTalent(booking),
                            cc: ['booking@set-cast.com']
                        };
                        
                        await emailService.sendEmail(emailData);
                        console.log(`✅ EMAIL SENT successfully for booking ${bookingId} (no talent data)`);
                    }
                } catch (error) {
                    console.warn(`⚠️ EMAIL ERROR for booking ${bookingId}:`, error.message);
                    emailError = error.message;
                }
            } else {
                console.log(`🔍 DEBUG: Email not sent for booking ${bookingId} - notifyTalent disabled`);
            }
            
            // Show success message (with email warning if needed)
            let message = "Talent released successfully.";
            if (emailError) {
                message += `\n\nNote: Email notification failed to send: ${emailError}`;
            }
            alert(message);
            window.location.reload();
            
            setIsLoading(false);
        } else {
            console.log("You have cancelled the operation.");
        }
    };

    // Helper function to create HTML content for team confirmation email
    const createEmailContentForTeamConfirmation = (booking, talent) => {
        const startDate = booking.start_date ? new Date(booking.start_date).toLocaleDateString() : 'N/A';
        const endDate = booking.end_date ? new Date(booking.end_date).toLocaleDateString() : 'N/A';
        const talentName = talent ? talent.name : 'Unknown';
        const position = typeof booking.position === 'object' && booking.position ? booking.position.label : (booking.position || 'N/A');
        
        return `
            Dear Team,
            <p>Planning Orendt has accepted the booking and the Order has been confirmed</p>
            <p>Start Date: <span>${startDate}</span></p>
            <p>End Date: <span>${endDate}</span></p>
            <p>Freelancer: ${talentName}</p>
            <p>Position: ${position}</p>
            <p>KPI: ${(() => {
              const kpiValue = booking.kpi;
              if (kpiValue === null || kpiValue === undefined || kpiValue === '') {
                return 'N/A';
              }
              
              // Handle objects (like location objects that might be in the wrong field)
              if (typeof kpiValue === 'object' && kpiValue !== null) {
                return 'N/A';
              }
              
              // Convert to number and round to integer
              const numericValue = parseFloat(kpiValue);
              if (!isNaN(numericValue)) {
                return Math.round(numericValue);
              }
              
              // If it's not a valid number, return 'N/A'
              return 'N/A';
            })()}</p>
            <p>Check all the booking in <a href="https://app.set-cast.com/">https://app.set-cast.com/</a></p>
        `;
    };

    // Helper function to create HTML content for talent confirmation email
    const createEmailContentForTalentConfirmation = (booking, talent) => {
        const startDate = booking.start_date ? new Date(booking.start_date).toLocaleDateString() : 'N/A';
        const rate = booking.custom_rate || talent.daily_rate || 'N/A';
        const talentName = talent ? talent.name : 'Unknown';
        const position = typeof booking.position === 'object' && booking.position ? booking.position.label : (booking.position || 'N/A');
        const client = typeof booking.client === 'object' && booking.client ? booking.client.label : (booking.client || 'N/A');
        
        return `
            Dear ${talentName},
            <p>We are happy to confirm you for the following production. Please find below the details:</p>
            <p>STATUS: CONFIRMATION</p>
            <p>Talent: ${talentName}</p>
            <p>FUNCTION: ${position}</p>
            <p>CLIENT: ${client}</p>
            <p>SET: ${booking.set || 'N/A'}</p>
            <p>DATE/PERIOD: <span>${startDate}</span></p>
            <p>DAYRATE: ${rate}</p>
            <p>KPI: ${(() => {
              const kpiValue = booking.kpi;
              if (kpiValue === null || kpiValue === undefined || kpiValue === '') {
                return 'N/A';
              }
              
              // Handle objects (like location objects that might be in the wrong field)
              if (typeof kpiValue === 'object' && kpiValue !== null) {
                return 'N/A';
              }
              
              // Convert to number and round to integer
              const numericValue = parseFloat(kpiValue);
              if (!isNaN(numericValue)) {
                return Math.round(numericValue);
              }
              
              // If it's not a valid number, return 'N/A'
              return 'N/A';
            })()}</p>
            <p>CALLTIME: ${booking.start_time || ''} - ${booking.end_time || ''}</p>
            <p>LOCATION: ${booking.location || 'N/A'}</p>
            <p>SETCast contact person: ${booking.proposed_by || ''}</p>
            <p>For questions regarding KPI, shooting schedules, callsheets and briefings, please contact planning@orendtstudios.com.</p>
            <p>In case of illness, please send an email directly to the distribution list planning@orendtstudios.com and krank@set-cast.com by 8:00 a.m. at the latest.</p>
            <p>Invoice details: please upload your invoice in Lano</p>
            <p>We wish you a successful production.</p>
            <p>Warm regards,</p>
            <p>Talents Management Team</p>
            <p>+49 40 39 834 667</p>
            <p>talents@set-cast.com</p>
            <p>LEGEND:</p>
            <p>B28 = Behringstrasse 28a, 22765 Hamburg</p>
            <p>TR4 = Tarpenring 4, 22419 Hamburg</p>
            <p>M= Holy-Allee 3, Metzingen, am Empfang melden</p>
            <p>OSB= Am Rosenkothen 2, 40880 Ratingen</p>
        `;
    };

    // Helper function to create HTML content for decline email
    const createEmailContentForDecline = (booking, talent) => {
        const startDate = booking.start_date ? new Date(booking.start_date).toLocaleDateString() : 'N/A';
        const talentName = talent ? talent.name : 'Unknown';
        const position = typeof booking.position === 'object' && booking.position ? booking.position.label : (booking.position || 'N/A');
        const client = typeof booking.client === 'object' && booking.client ? booking.client.label : (booking.client || 'N/A');
        
        return `
            Dear ${talentName},
            <p>Unfortunately, we have to release the 1st option we have for you:</p>
            <p>STATUS: RELEASE / CANCELLED</p>
            <p>FUNCTION: ${position}</p>
            <p>CLIENT: ${client}</p>
            <p>DATE/PERIOD: <span>${startDate}</span></p>
            <p>PLEASE keep all other 1st options!</p>
            <p>Hoping for your kind understanding. We will keep you in mind and contact you incase there is a spontaneous request.</p>
            <p>Warm regards,</p>
            <p>Talents Management Team</p>
            <p>+49 40 39 834 667</p>
            <p>talents@set-cast.com</p>
        `;
    };

    // Helper function to create HTML content for decline email when talent data is not found
    const createEmailContentForDeclineNoTalent = (booking) => {
        const startDate = booking.start_date ? new Date(booking.start_date).toLocaleDateString() : 'N/A';
        const position = typeof booking.position === 'object' && booking.position ? booking.position.label : (booking.position || 'N/A');
        const client = typeof booking.client === 'object' && booking.client ? booking.client.label : (booking.client || 'N/A');
        
        return `
            Dear Team,
            <p>A talent option has been released, but talent data could not be found:</p>
            <p>STATUS: RELEASE / CANCELLED</p>
            <p>BOOKING ID: ${booking.id}</p>
            <p>TALENT ID: ${booking.talent_id || 'Unknown'}</p>
            <p>FUNCTION: ${position || 'Unknown'}</p>
            <p>CLIENT: ${client || 'Unknown'}</p>
            <p>DATE/PERIOD: <span>${startDate}</span></p>
            <p>Please check the booking details and contact the talent directly if needed.</p>
            <p>Warm regards,</p>
            <p>Talents Management Team</p>
            <p>+49 40 39 834 667</p>
            <p>talents@set-cast.com</p>
        `;
    };

    const getUniqueOptions = (field) => {
        const options = Array.from(new Set(events.map(event => event[field])));
        return options.map(option => ({ value: option, label: option }));
    };

    // Helper function to get location display string
    const getLocationDisplay = (event) => {
        if (!event) return '';
        
        // Handle location as an object with properties
        if (event.location && typeof event.location === 'object') {
            return event.location.short || event.location.name || event.location.city || '';
        } else if (typeof event.location === 'string') {
            return event.location;
        } else if (event.studio) {
            return event.studio;
        }
        
        return '';
    };

    // Helper function to get location short for filters (using same logic)
    const getLocationShort = (event) => {
        return getLocationDisplay(event);
    };

    // Helper function to display KPI as integer
    const getKPIDisplay = (event) => {
        if (!event || !event.kpi) return "";
        
        const kpiValue = event.kpi;
        
        // Handle object KPI (shouldn't happen but just in case)
        if (typeof kpiValue === 'object' && kpiValue !== null) {
            return "";
        }
        
        // Convert to number and round to integer
        const numericValue = parseFloat(kpiValue);
        if (!isNaN(numericValue)) {
            return Math.round(numericValue).toString();
        }
        
        return "";
    };

    // Custom function for location options using short names
    const getLocationOptions = () => {
        const options = Array.from(new Set(events.map(event => getLocationShort(event)).filter(location => location !== '')));
        return options.map(option => ({ value: option, label: option }));
    };

    const filteredEvents = events.filter(
        event => {
            // Week filtering
            const eventDate = new Date(event.start);
            const { weekStart, weekEnd } = getWeekDates(currentWeek, currentYear);
            const isInCurrentWeek = eventDate >= weekStart && eventDate <= weekEnd;

            const matchesSearch = !searchTerm || (
                (event.boktitle && event.boktitle.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (event.set && event.set.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (event.talent && event.talent.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (event.status && event.status.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (event.client && event.client.toLowerCase().includes(searchTerm.toLowerCase()))
            );

            const matchesFilters = 
                (!selectedKPI || event.kpi === selectedKPI.value) &&
                (!selectedGrup || event.grup === selectedGrup.value) &&
                (!selectedClient || event.client === selectedClient.value) &&
                (!selectedSet || event.set === selectedSet.value) &&
                (!selectedLocation || getLocationShort(event) === selectedLocation.value) &&
                (!selectedPosition || event.position === selectedPosition.value) &&
                (!selectedTalent || event.talent === selectedTalent.value);

            return isInCurrentWeek && matchesSearch && matchesFilters;
        }
    );

    // Helper function to get current week number (ISO 8601)
    function getCurrentWeek() {
        const now = new Date();
        const start = new Date(now.getFullYear(), 0, 1);
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        // Set both dates to Thursday of their respective weeks
        // This ensures we get the correct ISO week number
        start.setDate(start.getDate() + (4 - (start.getDay() || 7)));
        today.setDate(today.getDate() + (4 - (today.getDay() || 7)));
        
        const weekNumber = Math.ceil((((today - start) / 86400000) + 1) / 7);
        return weekNumber;
    }

    // Helper function to get ISO week number
    function getISOWeek(date) {
        const target = new Date(date.valueOf());
        const dayNr = (date.getDay() + 6) % 7;
        target.setDate(target.getDate() - dayNr + 3);
        const firstThursday = target.valueOf();
        target.setMonth(0, 1);
        if (target.getDay() !== 4) {
            target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
        }
        return 1 + Math.ceil((firstThursday - target) / 604800000);
    }

    // Helper function to get dates for a specific week and year
    function getWeekDates(weekNumber, year) {
        // Get January 4th of the year (this is always in week 1 according to ISO 8601)
        const jan4 = new Date(year, 0, 4);
        
        // Find the Monday of week 1
        const mondayOfWeek1 = new Date(jan4);
        mondayOfWeek1.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7));
        
        // Calculate the Monday of the specified week
        const weekStart = new Date(mondayOfWeek1);
        weekStart.setDate(mondayOfWeek1.getDate() + (weekNumber - 1) * 7);
        
        // Calculate the Sunday of the specified week
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        
        return { weekStart, weekEnd, year: weekStart.getFullYear() };
    }

    // Navigation functions
    const goToPreviousWeek = () => {
        if (currentWeek === 1) {
            // Go to last week of previous year
            setCurrentYear(currentYear - 1);
            setCurrentWeek(52); // Most years have 52 weeks
        } else {
            setCurrentWeek(currentWeek - 1);
        }
    };

    const goToNextWeek = () => {
        const { weekStart } = getWeekDates(currentWeek + 1, currentYear);
        const weekYear = weekStart.getFullYear();
        
        if (weekYear > currentYear) {
            // Moving to next year
            setCurrentYear(weekYear);
            setCurrentWeek(1);
        } else if (currentWeek >= 52) {
            // Check if year has 53 weeks
            const dec31 = new Date(currentYear, 11, 31);
            const lastWeek = getISOWeek(dec31);
            if (currentWeek < lastWeek) {
                setCurrentWeek(currentWeek + 1);
            } else {
                // Go to first week of next year
                setCurrentYear(currentYear + 1);
                setCurrentWeek(1);
            }
        } else {
            setCurrentWeek(currentWeek + 1);
        }
    };

    const goToCurrentWeek = () => {
        const now = new Date();
        setCurrentWeek(getISOWeek(now));
        setCurrentYear(now.getFullYear());
    };

    return (
        <div>
            {isLoading && <div>Processing...</div>}
            {error && <div style={{ color: 'red' }}>{error}</div>}
            
            {/* Week Navigation */}
            <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px', 
                marginBottom: '20px',
                padding: '10px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px'
            }}>
                <button 
                    className="button"
                    onClick={goToPreviousWeek}
                >
                    Previous Week
                </button>
                <div style={{ 
                    padding: '13px 16px', 
                    backgroundColor: '#fff',
                    border: '1px solid #000',
                    fontWeight: '600'
                }}>
                    Week {currentWeek} of {currentYear}
                </div>
                <button 
                    className="button"
                    onClick={goToNextWeek}
                >
                    Next Week
                </button>
                <button 
                    className="button"
                    onClick={goToCurrentWeek}
                >
                    Current Week
                </button>
            </div>
            
            <div style={{ marginTop: '40px', marginBottom: '60px' }}>
                <input
                    type="text"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={handleChange}
                    className='inputtext search'
                />
            </div>

            <div style={{ marginBottom: '60px', display: 'flex', columnGap: '10px'}}>
                <Select
                    options={getUniqueOptions('kpi')}
                    onChange={setSelectedKPI}
                    placeholder="Select KPI"
                    isClearable
                    styles={customStyles}
                />
                <Select
                    options={getUniqueOptions('set')}
                    onChange={setSelectedSet}
                    placeholder="Select Set"
                    isClearable
                    styles={customStyles}
                />
                <Select
                    options={getUniqueOptions('position')}
                    onChange={setSelectedPosition}
                    placeholder="Select Position"
                    isClearable
                    styles={customStyles}
                />
                <Select
                    options={getLocationOptions()}
                    onChange={setSelectedLocation}
                    placeholder="Select Location"
                    isClearable
                    styles={customStyles}
                />
                <Select
                    options={getUniqueOptions('talent')}
                    onChange={setSelectedTalent}
                    placeholder="Select Talent"
                    isClearable
                    styles={customStyles}
                />
            </div>
            <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center' , marginBottom: '20px'}}>
                <button className="button-red" onClick={declineSelectedTalents} disabled={isLoading}>
                    Release Selected Talents
                </button>
                <div style={{ flexBasis: "40px", marginLeft: '10px', width: '100%' }} className="custom-checkbox">
                    <input
                        type="checkbox"
                        id="notify-talent-checkbox"
                        checked={notifyTalent}
                        onChange={() => {
                            console.log(`🔍 CHECKBOX CHANGED: notifyTalent is now ${!notifyTalent}`);
                            setNotifyTalent(!notifyTalent);
                        }}
                    />
                    <label htmlFor="notify-talent-checkbox" style={{ whiteSpace: 'nowrap' }}>
                        Notify Talent on Release/Decline (Current: {notifyTalent ? 'ON' : 'OFF'})
                    </label>
                </div>
            </div>

            <div className='content-wrapper content-wrapper-talent-list'>
                <div className='list-tasks-header'>
                    <div style={{ flexBasis: '200px' }}>start/end</div>
                    <div style={{ flexBasis: '150px' }}>set</div>
                    <div style={{ flexBasis: '50px' }}>kpi</div>
                    <div style={{ flexBasis: '100px' }}>pos.</div>
                    <div style={{ flexBasis: '100px' }}>option</div>
                    <div style={{ flexBasis: '150px' }}>talent</div>
                    <div style={{ flexBasis: '100px' }}>status</div>
                    <div style={{ flexBasis: '100px' }}>location</div>
                    <div style={{ flexBasis: '200px' }}>client</div>
                    <div style={{ flexBasis: '120px' }}>Actions</div>
                </div>
            </div>

            {filteredEvents.map(event => (
                <div 
                    className='list-tasks-item' 
                    key={event.id}
                    style={{
                        backgroundColor: event.group_color || event.groupColor || '#ffffff',
                        borderLeft: event.group_color || event.groupColor ? `4px solid ${event.group_color || event.groupColor}` : 'none'
                    }}
                >
                    <div style={{ flexBasis: "40px" }} className="custom-checkbox">
                        <input
                            type="checkbox"
                            id={`checkbox-${event.id}`}
                            checked={!!selectedOrders[event.id]}
                            onChange={() => handleCheckboxChange(event.id)}
                            disabled={isLoading}
                        />
                        <label htmlFor={`checkbox-${event.id}`}></label>
                    </div>
                    <div style={{ flexBasis: '200px' }}>
                        <div>
                            {new Date(event.start).toLocaleDateString()} - {new Date(event.end).toLocaleDateString()}
                        </div>
                        {event.created_at && (
                            <div style={{ 
                                fontSize: '0.75rem', 
                                color: '#666', 
                                marginTop: '2px',
                                fontStyle: 'italic'
                            }}>
                                Created: {new Date(event.created_at).toLocaleDateString()} {new Date(event.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </div>
                        )}
                    </div>
                    <div style={{ flexBasis: '150px' }}>{event.set}</div>
                    <div style={{ flexBasis: '50px' }}>{getKPIDisplay(event)}</div>
                    <div style={{ flexBasis: '100px' }}>{event.position}</div>
                    <div style={{ flexBasis: '100px' }}>{event.option}</div>
                    <div style={{ flexBasis: '150px' }}>
                        {event.talent || event.talent_name || event.talentName || 'No Talent'}
                        {!event.talent && !event.talent_name && !event.talentName && (
                            <div style={{ fontSize: '0.75rem', color: '#999', fontStyle: 'italic' }}>
                                Debug: {JSON.stringify(event).substring(0, 100)}...
                            </div>
                        )}
                    </div>
                    <div style={{ flexBasis: '100px' }}>{event.status}</div>
                    <div style={{ flexBasis: '100px' }}>{getLocationDisplay(event)}</div>
                    <div style={{ flexBasis: '200px' }}>{event.client}</div>
                    <div style={{ flexBasis: '120px', display: 'flex', gap: '8px' }}>
                     
                        <button
                            className="edit-buttons"
                            onClick={() => declineproposedtalent(event.id)}
                            disabled={isLoading}
                            title="Decline Booking"
                            style={{ background: 'transparent', border: 'none' }}
                            >
                              <img 
                                src={declineIcon} 
                                alt="Delete" 
                                className="edit-container" 
                                style={{ filter: 'invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(82%) contrast(97%)',width: '24px', height: '24px' }}
                              />
                        </button>
                    </div>
                </div>
            ))}

            <div style={{ marginTop: '80px' }}>
                <p><i>Note: Declining will release the talent.</i></p>
            </div>
        </div>
    );
};

export default TalentsOnHoldList;
